<template>
    <div class="fb-path">
        <span v-if="scheme" class="fb-path-scheme">{{ scheme }}://</span>
        <nav class="fb-path-trail" aria-label="breadcrumb">
            <template v-for="(item, index) in breadcrumbItems" :key="index">
                <span v-if="index" class="fb-path-sep">/</span>
                <a v-if="index < breadcrumbItems.length - 1" href="#" class="fb-path-link"
                    @click.prevent="goTo(item, index)">{{ item.name }}</a>
                <span v-else class="fb-path-current">{{ item.name }}</span>
            </template>
        </nav>
        <button v-if="fullPath" type="button" class="fb-icon-btn fb-icon-btn-sm fb-path-copy"
            :title="copied ? 'Copied' : 'Copy path'" @click="copyPath">
            <i :class="copied ? 'fa-solid fa-check' : 'fa-regular fa-copy'"></i>
        </button>
    </div>
</template>

<script>
/*
 * The path bar for both file browsers: the bucket URI as it would be pasted into a
 * job config, with each segment navigable and the whole thing copyable. Owns the
 * folder-path rewriting that clicking a segment implies, because the trail is the
 * only record of how the current folder was reached.
 */
import { buildStoragePath, schemeForCredentialType } from "../../utils/fileBrowser";

export default {
    name: "FileBrowserPath",
    props: {
        breadcrumbItems: {
            type: Array,
            default: () => [],
        },
        // the routed browser re-derives the clicked segment from the route; the inline
        // one fetches the rewritten path directly
        showRoute: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            copied: false,
            copyTimer: null,
        };
    },
    computed: {
        scheme() {
            return schemeForCredentialType(this.$store.state.amdp.selectedCredentialType);
        },
        fullPath() {
            if (!this.breadcrumbItems.length) return "";
            return this.pathForSegments(this.breadcrumbItems);
        },
    },
    methods: {
        pathForSegments(segments) {
            if (!segments.length) return "";
            // a segment named "/" stands for an object key that is literally a slash, so
            // it contributes an empty name - the separator it still needs comes from the
            // join, which is why the trailing slash is added on segment count rather than
            // on the joined key being non-empty
            const below = segments.slice(1).map((item) => (item.name === "/" ? "" : item.name));
            if (!below.length) return buildStoragePath(this.scheme, segments[0].name, "");
            // a folder key ends in a slash, an object key does not
            const trailing = segments[segments.length - 1].isFile ? "" : "/";
            return buildStoragePath(this.scheme, segments[0].name, `${below.join("/")}${trailing}`);
        },
        goTo(item, index) {
            this.trimTo(index);
            if (!this.showRoute) {
                this.$emit("onBreadcrumbClick");
                return;
            }
            // crumbs recorded by Datatable carry no query of their own - keep the page's
            // current one (minus the one-shot refresh token) so context is not dropped
            const { refresh, ...carried } = this.$route.query;
            this.$router.push({ ...item.route, query: { ...carried, ...(item.route?.query || {}) } });
        },
        trimTo(index) {
            const breadcrumbs = this.$store.state.breadcrumbs;
            this.$store.dispatch("setBreadcrumbs", breadcrumbs.slice(0, index + 1));
            // The routed browser re-appends the clicked segment from the route id, so its
            // path is rebuilt only up to the parent. The inline browser fetches the path
            // as-is and needs the clicked segment included.
            const segments = breadcrumbs.slice(0, this.showRoute ? index : index + 1);
            // without a bucket segment we are back at the root and the folder view
            // rebuilds the path from the route
            this.$store.dispatch("amdp/setFolderPath", segments.length ? this.pathForSegments(segments) : "");
        },
        async copyPath() {
            try {
                await navigator.clipboard.writeText(this.fullPath);
            } catch (e) {
                // clipboard is unavailable outside a secure context - nothing to recover
                console.error("Could not copy path: ", e);
                return;
            }
            this.copied = true;
            clearTimeout(this.copyTimer);
            this.copyTimer = setTimeout(() => { this.copied = false; }, 1600);
        },
    },
    unmounted() {
        clearTimeout(this.copyTimer);
    },
    emits: ["onBreadcrumbClick"],
};
</script>

<style scoped>
.fb-path {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    min-width: 0;
    width: 100%;
    font-size: 13px;
}

/* The scheme is context, not a step in the trail - it reads as a tag */
.fb-path-scheme {
    flex: none;
    padding: 0.15rem 0.45rem;
    border-radius: var(--fb-radius-sm);
    background: var(--fb-primary-dim);
    color: var(--fb-primary);
    font-family: var(--fb-font-mono);
    font-size: 11.5px;
    font-weight: 600;
    line-height: 1.5;
}

.fb-path-trail {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.3rem;
    min-width: 0;
    font-family: var(--fb-font-mono);
}

.fb-path-sep {
    color: var(--fb-text-dim);
}

.fb-path-link {
    padding: 0.1rem 0.3rem;
    border-radius: 4px;
    color: var(--fb-text-muted);
    text-decoration: none;
    transition: color var(--fb-transition), background var(--fb-transition);
}

.fb-path-link:hover {
    color: var(--fb-primary);
    background: var(--fb-primary-dim);
}

.fb-path-current {
    padding: 0.1rem 0.3rem;
    color: var(--fb-text);
    font-weight: 600;
}

.fb-path-copy {
    margin-left: auto;
}
</style>
